import { getFileDataUrl } from 'lib/library';
import {
  clinicImageTypes,
  clinicModelTypes,
  projectEventType,
  scanFileExtionsion,
} from 'lib/mapper';
import { useCallback, useContext } from 'react';
import { AppActions, FileActions, ProjectActions } from 'store/actionCreators';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { ProjectContext } from 'contexts/project/ProjectContext';
import { IConvertFile } from 'lib/types/clinic/fileTypes';
import { fetchProjectFileDownloadUrl } from 'api/file';
import { axs } from 'api/config/axiosConfig';
import { v4 as uuid } from 'uuid';
import { useProjectFileStore } from 'store/local/projectFile';
import { downloadFile, downloadFileWithElectron, newDownloadFileWithElectron } from 'store/utils';
import { useProject } from './useProject';
import { handleClearPmsData } from 'containers/base/PmsConnect';
import { useTranslation } from 'react-i18next';

export const useProjectFiles = () => {
  const { ipcRenderer } = window;

  const { projectId, selectedSyncPartner, setNewSyncPartner, queryPageTab, setQueryPageTab } =
    useProjectDetailStore();
  const {
    removeType,
    uploadLocalFiles,
    setUploadLocalFiles,
    setUploadShareType,
    setIsCheckboxChecked,
    setIsOwnerStorage,
    setIsShareStorage,
    setIsLocalStorage,
    setIsOpenRemoveModal,
    setLocalFileDeleteSuccess,
    isOpenDuplicateModal,
    setIsOpenDuplicateModal,
    isOpenDuplicateModalType,
    setIsOpenDuplicateModalType,
    isPmsView,
    setIsPmsView,
    setModelError,
  } = useProjectFileStore();

  const { projectCode, projectInfo, user, scanFileExists } = useProject();

  const { classifyClinicFiles, clinicCloudFiles, clinicLocalFiles } = useContext(ProjectContext);

  const {
    clinicViewModelFiles,
    clinicViewImageFiles,
    clinicViewHtmlFiles,
    checkedClinicFiles,
    upperFiles,
    lowerFiles,
    occlusionFiles,
    photoFiles,
    etcFiles,
    noneFiles,
    clinicLocalFileList,
    deleteLocalFileList,
    clinicLoadLocalFilesDataList,
    deleteCloudFileList,
    clinicSortedAllLocalFileList,
    scanShowiles,
  } = classifyClinicFiles;

  const {
    cloudUpperPly,
    cloudUpperStl,
    cloudUpperObj,
    cloudLowerPly,
    cloudLowerStl,
    cloudLowerObj,
    cloudOcclusionPly,
    cloudOcclusionStl,
    cloudOcclusionObj,
    cloudEtcModelPly,
    cloudEtcModelStl,
    cloudEtcModelObj,
    cloudPhotoFiles,
    cloudEtcFiles,
  } = clinicCloudFiles;

  const {
    localUpperPly,
    localUpperStl,
    localUpperObj,
    localLowerPly,
    localLowerStl,
    localLowerObj,
    localOcclusionPly,
    localOcclusionStl,
    localOcclusionObj,
    localEtcModelPly,
    localEtcModelStl,
    localEtcModelObj,
    localPhotoFiles,
    localEtcFiles,
  } = clinicLocalFiles;

  const handleAddLocalFiles = (data: any) => {
    if (!ipcRenderer) return;
    const { dropFiles } = data;
    setUploadLocalFiles(dropFiles);
    // TODO: checkbox clear
  };

  const duplicateFileOverwriteController = (
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc, curr) => {
        if (!!curr.isMyFile && curr.originName === file.originName) {
          const data = {
            ...curr,
            ...file,
            id: curr.id,
            compareKey: curr.compareKey,
            isLocal: 1,
            isLocalStorage: true,
            isOwner: curr.isOwner,
            isOwnerStorage: curr.isOwnerStorage,
            isShare: curr.isShare,
            isShareStorage: curr.isShareStorage,
          };
          return acc.concat(data);
        } else {
          return acc.concat({ ...curr });
        }
      }, [] as IConvertFile[]);
    });
  };

  const duplicateLocalFile = (files: IConvertFile[]) => {
    console.log('HDS] duplicateLocalFile _______ ', files);
    files.map((file: IConvertFile) => {
      switch (file.fileType) {
        case 'UPPER':
          duplicateFileOverwriteController(upperFiles, file);
          return;
        case 'LOWER':
          duplicateFileOverwriteController(lowerFiles, file);
          return;
        case 'OCCLUSION':
          duplicateFileOverwriteController(occlusionFiles, file);
          return;
        case 'NONE':
          duplicateFileOverwriteController(noneFiles, file);
          return;
        case 'PHOTO':
          duplicateFileOverwriteController(photoFiles, file);
          return;
        case 'ETC':
          duplicateFileOverwriteController(etcFiles, file);
          return;
        default:
          return;
      }
    });

    if (ipcRenderer) {
      localFileUploadIpc(files);
    }
  };

  const localFileUploadIpc = (files: IConvertFile[]) => {
    const returnValue = files?.reduce((acc: any[], curr: any, index: number) => {
      const extension = curr.name.substring(curr.name.lastIndexOf('.'));
      const obj = {
        fileName: curr.name,
        filePath: !!curr.path ? curr.path : curr.file.path,
        extension,
      };
      return acc.concat(obj);
    }, []);
    const submitData = {
      projectId,
      files: returnValue,
    };
    if (ipcRenderer) {
      ipcRenderer.send('projectFileLocalUpload', submitData);
      //NOTE: File upload시, File 종류에 따른 타임라인 변경
      // .stl => SCAN
      // _cad.stl, .pts => CAD
      // .stl, _cad.stl, .pts => SCAN -> CAD
      const isExistCadFile = returnValue.map((item: any) => {
        const fileSplit = item.fileName.split('_');
        const isCadStl = fileSplit.some((splitData: any) => {
          // '_'로 split후 'cad.stl'이 존재하는지 여부로 "_cad.stl" 유/무 확인
          return splitData.toLowerCase() === 'cad.stl';
        });
        const isCadPts = item.extension.toLowerCase() === '.pts';
        return !!isCadStl || !!isCadPts;
      });
      const isExsitSacnFile = returnValue.map((item: any) => {
        return scanFileExtionsion.some((extenstion: any) => {
          return extenstion === item.extension.toLowerCase();
        });
      });

      const scanFileFlag = isExsitSacnFile.some((isScanFile: any) => {
        return !!isScanFile;
      });
      const cadFileFlag = isExistCadFile.some((isCadFile: any) => {
        return !!isCadFile;
      });
      // .stl, _cad.stl, .pts 모두 존재하는 경우 둘 다 실행
      if (!!scanFileFlag) {
        // .stl => SCAN
        ProjectActions.edit_timeline_request({
          projectCode,
          stage: 2,
        });
      }
      if (!!cadFileFlag) {
        // _cad.stl, .pts => CAD
        setTimeout(() => {
          ProjectActions.edit_timeline_request({
            projectCode,
            stage: 3,
          });
          // 시점 이슈로 CAD인 경우 1초뒤 실행
        }, 1000);
      }
    }
  };

  const findFileOption = (fileName: any) => {
    const extension = fileName.substring(fileName?.lastIndexOf('.') + 1).toLocaleUpperCase();
    if (clinicModelTypes.includes(extension)) {
      if (fileName.includes(projectId)) {
        if (fileName.toLowerCase().includes('upper')) {
          const scanOption = fileName.toLowerCase().includes(`upperjaw-situ`)
            ? 'PREOP'
            : fileName.toLowerCase().includes(`upper-marker`)
            ? 'SCANBODY'
            : null;

          return {
            fileType: 'UPPER',
            scanOption: scanOption,
          };
        } else if (fileName.toLowerCase().includes('lower')) {
          const scanOption = fileName.toLowerCase().includes(`lowerjaw-situ`)
            ? 'PREOP'
            : fileName.toLowerCase().includes(`lower-marker`)
            ? 'SCANBODY'
            : null;
          return {
            fileType: 'LOWER',
            scanOption: scanOption,
          };
        } else {
          const occluNumber = fileName.match(/occlusion(\d+)/);
          const scanOption = `OCCLU${occluNumber[1]}`;
          return {
            fileType: 'OCCLUSION',
            scanOption: scanOption,
          };
        }
      } else {
        const scanOption =
          fileName.toLowerCase().includes(`upperjaw-situ`) ||
          fileName.toLowerCase().includes(`lowerjaw-situ`)
            ? 'PREOP'
            : fileName.toLowerCase().includes(`upper-marker`) ||
              fileName.toLowerCase().includes(`lower-marker`)
            ? 'SCANBODY'
            : null;

        return {
          fileType: 'NONE',
          scanOption: scanOption,
        };
      }
    } else if (clinicImageTypes.includes(extension)) {
      return {
        fileType: 'PHOTO',
        scanOption: null,
      };
    } else {
      return {
        fileType: 'ETC',
        scanOption: null,
      };
    }
  };

  const updateLocalFileList = (items: any) => {
    // TODO: isView, isCheck 초기화
    const returnValue = items?.reduce((acc: any[], curr: any, index: number) => {
      const { file, name, size, path } = curr;
      const originName = curr.name.normalize('NFC');

      const extension = originName.substring(originName?.lastIndexOf('.') + 1).toLocaleUpperCase();
      const fileOption = findFileOption(originName);
      console.log('HDS] fileOption _______ ', fileOption);

      const type = name.slice(name.lastIndexOf('.') + 1);
      const obj = {
        ...curr,
        id: uuid(),
        compareKey: uuid(),
        isChecked: false,
        isView: false,
        originName: originName,
        name: originName,
        file: !!file ? file : curr,
        size,
        type,
        isLast: false,
        opacity: 100,
        isWireframe: false,
        isLocal: 1,
        isLocalStorage: true,
        isOwner: 0,
        isOwnerStorage: false,
        isShare: 0,
        isShareStorage: false,
        isMyFile: true,
        fileType: fileOption.fileType,
        fileExtension: extension,
        scanOption: fileOption.scanOption,
      };

      return acc.concat(obj);
    }, []);
    return returnValue;
  };

  const handleCombineCheckedFile = (isAdd: boolean, checkedFile: IConvertFile) => {
    if (!!isAdd) {
      checkedClinicFiles.setValue((draft: IConvertFile[]) => [...draft, checkedFile]);
    } else {
      const filter = checkedClinicFiles.value.filter(
        (draft: IConvertFile) => draft.compareKey !== checkedFile.compareKey,
      );
      checkedClinicFiles.setValue(filter);
    }
  };

  const clinicOriginFilesIsCheckedController = (
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.compareKey === file.compareKey) {
          curr.isChecked = !file.isChecked;
        }
        return acc.concat(curr);
      }, []);
    });
  };

  const clinicFilesIsCheckedController = (
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.compareKey === file.compareKey) {
          handleCombineCheckedFile(!file.isChecked, file);
        }
        return acc.concat(curr);
      }, []);
    });
  };

  const clinicFilesClearCheckedController = (fileList: {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  }) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isChecked = false;
        return acc.concat(curr);
      }, []);
    });
  };

  const handleClearAllCheckbox = () => {
    // clinicFilesClearCheckedController(upperFiles);
    // clinicFilesClearCheckedController(lowerFiles);
    // clinicFilesClearCheckedController(occlusionFiles);
    // clinicFilesClearCheckedController(noneFiles);
    // clinicFilesClearCheckedController(photoFiles);
    // clinicFilesClearCheckedController(etcFiles);
    clinicFilesClearCheckedController(cloudUpperPly);
    clinicFilesClearCheckedController(cloudUpperStl);
    clinicFilesClearCheckedController(cloudUpperObj);
    clinicFilesClearCheckedController(localUpperPly);
    clinicFilesClearCheckedController(localUpperStl);
    clinicFilesClearCheckedController(localUpperObj);
    clinicFilesClearCheckedController(cloudLowerPly);
    clinicFilesClearCheckedController(cloudLowerStl);
    clinicFilesClearCheckedController(cloudLowerObj);
    clinicFilesClearCheckedController(localLowerPly);
    clinicFilesClearCheckedController(localLowerStl);
    clinicFilesClearCheckedController(localLowerObj);
    clinicFilesClearCheckedController(cloudOcclusionPly);
    clinicFilesClearCheckedController(cloudOcclusionStl);
    clinicFilesClearCheckedController(cloudOcclusionObj);
    clinicFilesClearCheckedController(localOcclusionPly);
    clinicFilesClearCheckedController(localOcclusionStl);
    clinicFilesClearCheckedController(localOcclusionObj);
    clinicFilesClearCheckedController(cloudEtcModelPly);
    clinicFilesClearCheckedController(cloudEtcModelStl);
    clinicFilesClearCheckedController(cloudEtcModelObj);
    clinicFilesClearCheckedController(localEtcModelPly);
    clinicFilesClearCheckedController(localEtcModelStl);
    clinicFilesClearCheckedController(localEtcModelObj);
    clinicFilesClearCheckedController(cloudPhotoFiles);
    clinicFilesClearCheckedController(localPhotoFiles);
    clinicFilesClearCheckedController(cloudEtcFiles);
    clinicFilesClearCheckedController(localEtcFiles);

    checkedClinicFiles.setValue([]);
    setIsCheckboxChecked(false);
    setIsOwnerStorage(false);
    setIsShareStorage(false);
    setIsLocalStorage(false);
  };

  const handleChangeCheckbox = (file: IConvertFile) => {
    switch (file.fileType) {
      case 'UPPER':
        clinicFilesIsCheckedController(upperFiles, file);
        clinicOriginFilesIsCheckedController(cloudUpperPly, file);
        clinicOriginFilesIsCheckedController(cloudUpperStl, file);
        clinicOriginFilesIsCheckedController(cloudUpperObj, file);
        clinicOriginFilesIsCheckedController(localUpperPly, file);
        clinicOriginFilesIsCheckedController(localUpperStl, file);
        clinicOriginFilesIsCheckedController(localUpperObj, file);
        return;
      case 'LOWER':
        clinicFilesIsCheckedController(lowerFiles, file);
        clinicOriginFilesIsCheckedController(cloudLowerPly, file);
        clinicOriginFilesIsCheckedController(cloudLowerStl, file);
        clinicOriginFilesIsCheckedController(cloudLowerObj, file);
        clinicOriginFilesIsCheckedController(localLowerPly, file);
        clinicOriginFilesIsCheckedController(localLowerStl, file);
        clinicOriginFilesIsCheckedController(localLowerObj, file);
        return;
      case 'OCCLUSION':
        clinicFilesIsCheckedController(occlusionFiles, file);
        clinicOriginFilesIsCheckedController(cloudOcclusionPly, file);
        clinicOriginFilesIsCheckedController(cloudOcclusionStl, file);
        clinicOriginFilesIsCheckedController(cloudOcclusionObj, file);
        clinicOriginFilesIsCheckedController(localOcclusionPly, file);
        clinicOriginFilesIsCheckedController(localOcclusionStl, file);
        clinicOriginFilesIsCheckedController(localOcclusionObj, file);
        return;
      case 'NONE':
        clinicFilesIsCheckedController(noneFiles, file);
        clinicOriginFilesIsCheckedController(cloudEtcModelPly, file);
        clinicOriginFilesIsCheckedController(cloudEtcModelStl, file);
        clinicOriginFilesIsCheckedController(cloudEtcModelObj, file);
        clinicOriginFilesIsCheckedController(localEtcModelPly, file);
        clinicOriginFilesIsCheckedController(localEtcModelStl, file);
        clinicOriginFilesIsCheckedController(localEtcModelObj, file);
        return;
      case 'PHOTO':
        clinicFilesIsCheckedController(photoFiles, file);
        clinicOriginFilesIsCheckedController(cloudPhotoFiles, file);
        clinicOriginFilesIsCheckedController(localPhotoFiles, file);

        return;
      case 'ETC':
        clinicFilesIsCheckedController(etcFiles, file);
        clinicOriginFilesIsCheckedController(cloudEtcFiles, file);
        clinicOriginFilesIsCheckedController(localEtcFiles, file);
        return;
      default:
        return;
    }
  };

  const fetchCloudModelFile = async (file: IConvertFile) => {
    if (!file.cloudDataIdx) return;
    const resourceMtlFileListValue = etcFiles.value.filter((item: IConvertFile) =>
      file.resourceFileList?.includes(item.cloudDataIdx),
    );
    const resourceImageFileListValue = photoFiles.value.filter((item: IConvertFile) =>
      file.resourceFileList?.includes(item.cloudDataIdx),
    );
    let modelFileUrl = '';
    let mtlFileUrl = '';
    let textureFileUrl = '';
    try {
      const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: file.cloudDataIdx });
      const { signedUrl } = data;
      if (!signedUrl) return;
      modelFileUrl = signedUrl;
    } catch (error) {
      AppActions.show_toast({
        type: 'error',
        message: (error as { message: string })?.message,
      });
    }

    const mtlCloudIdx = resourceMtlFileListValue.find(
      (item: any) => item.type === 'mtl',
    )?.cloudDataIdx;
    const textureCloudIdx = resourceImageFileListValue.find((item: any) => {
      return clinicImageTypes.includes(item.type.toUpperCase());
    })?.cloudDataIdx;

    if (!!mtlCloudIdx) {
      try {
        const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: mtlCloudIdx });
        const { signedUrl } = data;
        if (!signedUrl) return;
        mtlFileUrl = signedUrl;
      } catch (error) {
        AppActions.show_toast({
          type: 'error',
          message: (error as { message: string })?.message,
        });
      }
    }

    if (!!textureCloudIdx) {
      try {
        const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: textureCloudIdx });
        const { signedUrl } = data;
        if (!signedUrl) return;
        textureFileUrl = signedUrl;
      } catch (error) {
        AppActions.show_toast({
          type: 'error',
          message: (error as { message: string })?.message,
        });
      }
    }

    const fileData = {
      ...file,
      url: modelFileUrl,
      textureUrl: !!textureFileUrl ? textureFileUrl : '',
      mtlUrl: !!mtlFileUrl ? mtlFileUrl : '',
      material: undefined,
      wireframe: undefined,
    };

    clinicViewModelFiles.setValue((draft: IConvertFile[]) => [...draft, fileData]);
  };

  const fetchLocalModelFile = async (file: IConvertFile) => {
    if (!ipcRenderer) return;
    console.log('HDS] fetchLocalModelFile ________ ', file);
    let modelFileData = '';
    let modelFileUrl = '';
    let mtlFileData = '';
    let mtlFileName = '';
    let textureFileData = '';
    let textureFileName = '';

    const fileName = file.name?.split('.')[0];
    etcFiles.value.find((item: any) => {
      if (item.name.split('.')[0] === fileName) {
        if (item.type === 'mtl') {
          mtlFileName = item.name;
        }
        if (clinicImageTypes.includes(item.type)) {
          textureFileName = item.name;
        }
      }
    });
    photoFiles.value.find((item: any) => {
      if (item.name.split('.')[0] === fileName) {
        textureFileName = item.name;
      }
    });

    try {
      const modelFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
        projectId,
        fileName: file.name,
      });

      modelFileData = modelFileLoad.fileData;
      modelFileUrl = modelFileLoad.fileUrl;
    } catch (error) {
      AppActions.show_toast({
        type: 'error',
        message: (error as { message: string })?.message,
      });
    }

    if (!!mtlFileName) {
      try {
        const mtlFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
          projectId,
          fileName: mtlFileName,
        });
        mtlFileData = mtlFileLoad.fileUrl;
        // const fileBlob = new Blob([mtlFileLoad?.fileData], {
        //   type: 'application/x-www-form-urlencoded',
        // });

        // const fileUrl = URL.createObjectURL(fileBlob);
        // mtlFileData = fileUrl;
      } catch (error) {
        AppActions.show_toast({
          type: 'error',
          message: (error as { message: string })?.message,
        });
      }
    }
    if (!!textureFileName) {
      try {
        const textureFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
          projectId,
          fileName: textureFileName,
        });
        textureFileData = textureFileLoad.fileUrl;
        // const fileBlob = new Blob([textureFileLoad?.fileData], {
        //   type: 'application/x-www-form-urlencoded',
        // });

        // const fileUrl = URL.createObjectURL(fileBlob);
        // textureFileData = fileUrl;
      } catch (error) {
        AppActions.show_toast({
          type: 'error',
          message: (error as { message: string })?.message,
        });
      }
    }

    const fileData = {
      ...file,
      fileData: modelFileData,
      url: modelFileUrl,
      textureUrl: !!textureFileData ? textureFileData : '',
      mtlUrl: !!mtlFileData ? mtlFileData : '',
      material: undefined,
      wireframe: undefined,
    };

    clinicViewModelFiles.setValue((draft: IConvertFile[]) => [...draft, fileData]);
  };

  const fileOpacityInitController = (
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (
          (curr.cloudDataIdx === file.cloudDataIdx || curr.id === file.id) &&
          curr.originName === file.originName
        ) {
          curr.opacity = 100;
        }
        return acc.concat(curr);
      }, []);
    });
  };
  const handleInitFileOpacity = (file: IConvertFile) => {
    switch (file.fileType) {
      case 'UPPER':
        // fileOpacityInitController(upperFiles, file);
        fileOpacityInitController(cloudUpperPly, file);
        fileOpacityInitController(cloudUpperStl, file);
        fileOpacityInitController(cloudUpperObj, file);
        fileOpacityInitController(localUpperPly, file);
        fileOpacityInitController(localUpperStl, file);
        fileOpacityInitController(localUpperObj, file);
        return;
      case 'LOWER':
        // fileOpacityInitController(lowerFiles, file);
        fileOpacityInitController(cloudLowerPly, file);
        fileOpacityInitController(cloudLowerStl, file);
        fileOpacityInitController(cloudLowerObj, file);
        fileOpacityInitController(localLowerPly, file);
        fileOpacityInitController(localLowerStl, file);
        fileOpacityInitController(localLowerObj, file);
        return;
      case 'OCCLUSION':
        // fileOpacityInitController(occlusionFiles, file);
        fileOpacityInitController(cloudOcclusionPly, file);
        fileOpacityInitController(cloudOcclusionStl, file);
        fileOpacityInitController(cloudOcclusionObj, file);
        fileOpacityInitController(localOcclusionPly, file);
        fileOpacityInitController(localOcclusionStl, file);
        fileOpacityInitController(localOcclusionObj, file);
        return;
      case 'NONE':
        // fileOpacityInitController(noneFiles, file);
        fileOpacityInitController(cloudEtcModelPly, file);
        fileOpacityInitController(cloudEtcModelStl, file);
        fileOpacityInitController(cloudEtcModelObj, file);
        fileOpacityInitController(localEtcModelPly, file);
        fileOpacityInitController(localEtcModelStl, file);
        fileOpacityInitController(localEtcModelObj, file);
        return;
      case 'PHOTO':
        // fileOpacityInitController(photoFiles, file);
        return;
      case 'ETC':
        // fileOpacityInitController(etcFiles, file);
        return;
      default:
        return;
    }
  };

  const handleCombineViewAutoFile = (isAdd: boolean, viewFile: IConvertFile) => {
    if (clinicModelTypes.includes(viewFile.fileExtension)) {
      // Model Files
      clinicViewImageFiles.setValue(null);
      clinicViewHtmlFiles.setValue(null);
      if (!!isAdd) {
        if (!viewFile.isOwner && !viewFile.isShare) {
          // cloud 데이터가 존재하는경우, cloud 데이터 우선
          fetchLocalModelFile(viewFile);
        } else {
          fetchCloudModelFile(viewFile);
        }
      } else {
        const filter = clinicViewModelFiles.value.filter(
          (draft: IConvertFile) => draft.compareKey !== viewFile.compareKey,
        );
        clinicViewModelFiles.setValue(filter);
        handleInitFileOpacity(viewFile);
      }
    } else if (clinicImageTypes.includes(viewFile.fileExtension)) {
      // Image Files
      clinicViewModelFiles.setValue([]);
      clinicViewHtmlFiles.setValue(null);
      if (!!isAdd) {
        if (!viewFile.isOwner && !viewFile.isShare) {
          fetchLocalFile(viewFile, true);
        } else {
          fetchCloudFile(viewFile, true);
        }
      } else {
        clinicViewImageFiles.setValue(null);
      }
    } else {
      // Html Files (another)
      clinicViewModelFiles.setValue([]);
      clinicViewImageFiles.setValue(null);
      if (!!isAdd) {
        if (!viewFile.isOwner && !viewFile.isShare) {
          fetchLocalFile(viewFile, false);
        } else {
          fetchCloudFile(viewFile, false);
        }
      } else {
        clinicViewHtmlFiles.setValue(null);
      }
    }
  };

  const handleCombineViewFile = (isAdd: boolean, viewFile: IConvertFile) => {
    if (clinicModelTypes.includes(viewFile.fileExtension)) {
      // Model Files
      clinicViewImageFiles.setValue(null);
      clinicViewHtmlFiles.setValue(null);
      console.log('THDS] handleCombineViewFile 실행 ');
      if (!!isAdd) {
        if (!viewFile.isOwner && !viewFile.isShare) {
          // cloud 데이터가 존재하는경우, cloud 데이터 우선
          fetchLocalModelFile(viewFile);
        } else {
          fetchCloudModelFile(viewFile);
        }
      } else {
        const filter = clinicViewModelFiles.value.filter(
          (draft: IConvertFile) => draft.compareKey !== viewFile.compareKey,
        );
        clinicViewModelFiles.setValue(filter);
        handleInitFileOpacity(viewFile);
      }
    } else if (clinicImageTypes.includes(viewFile.fileExtension)) {
      // Image Files
      clinicViewModelFiles.setValue([]);
      clinicViewHtmlFiles.setValue(null);
      if (!!isAdd) {
        if (!viewFile.isOwner && !viewFile.isShare) {
          fetchLocalFile(viewFile, true);
        } else {
          fetchCloudFile(viewFile, true);
        }
      } else {
        clinicViewImageFiles.setValue(null);
      }
    } else {
      // Html Files (another)
      clinicViewModelFiles.setValue([]);
      clinicViewImageFiles.setValue(null);
      if (!!isAdd) {
        if (!viewFile.isOwner && !viewFile.isShare) {
          fetchLocalFile(viewFile, false);
        } else {
          fetchCloudFile(viewFile, false);
        }
      } else {
        clinicViewHtmlFiles.setValue(null);
      }
    }
  };

  const handlePmsThumbnailAutoView = () => {
    if (!!isPmsView) return;
    console.log('HDS] handlePmsThumbnailAutoView scanShowiles _ ', scanShowiles.value);
    upperFiles.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (!curr.isView) {
          if (!!curr.isShow && curr.isShow === 1) {
            curr.isView = true;
            handleCombineViewFile(true, curr);
          } else {
            curr.isView = false;
          }
        }
        return acc.concat(curr);
      }, []);
    });
    lowerFiles.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (!curr.isView) {
          if (!!curr.isShow && curr.isShow === 1) {
            curr.isView = true;
            handleCombineViewFile(true, curr);
          } else {
            curr.isView = false;
          }
        }
        return acc.concat(curr);
      }, []);
    });
    occlusionFiles.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (!curr.isView) {
          if (!!curr.isShow && curr.isShow === 1) {
            curr.isView = true;
            handleCombineViewFile(true, curr);
          } else {
            curr.isView = false;
          }
        }
        return acc.concat(curr);
      }, []);
    });

    if (clinicViewModelFiles.value.length === scanShowiles.value.length) {
      setIsPmsView(true);
      handleClearPmsData();
    }
  };

  const handleScanFiilAutoViewer = async (
    files: IConvertFile[],
    type: 'UPPER' | 'LOWER' | 'OCCLUSION',
  ) => {
    console.log('HDS] handleScanFiilAutoViewer');
    const src = files ?? [];
    let localCount = 0;
    const toCombine: IConvertFile[] = [];

    // 1) 계산: next 상태와 count, 사이드이펙트 대상 수집
    const next = src.map((item, idx) => {
      if (item.isView) return item; // 이미 true면 변경 없음

      const shouldView = item.isShow === 1;

      if (shouldView) {
        localCount++;
        const updated = { ...item, isView: true };
        toCombine.push(updated);
        return updated;
      }

      // 필요 시 명시적으로 false 설정 (값이 같으면 원본 반환해 리렌더 최소화)
      return item.isView === false ? item : { ...item, isView: false };
    });

    // 2) 사이드이펙트: 중복 방지 후 실행 (id가 없을 수 있어 대체키 사용)
    const seen = new Set<string>();
    toCombine.forEach((f, idx) => {
      const key = f.id ?? (f as any).path ?? `${f.name ?? 'file'}#${idx}`;
      if (seen.has(key)) return;
      seen.add(key);
      handleCombineViewFile(true, f);
      if (type === 'UPPER') {
        clinicFilesIsViewerKeepOriginFileController(cloudUpperPly, f);
        clinicFilesIsViewerKeepOriginFileController(cloudUpperStl, f);
        clinicFilesIsViewerKeepOriginFileController(cloudUpperObj, f);
        clinicFilesIsViewerKeepOriginFileController(localUpperPly, f);
        clinicFilesIsViewerKeepOriginFileController(localUpperStl, f);
        clinicFilesIsViewerKeepOriginFileController(localUpperObj, f);
      }
      if (type === 'LOWER') {
        clinicFilesIsViewerKeepOriginFileController(cloudLowerPly, f);
        clinicFilesIsViewerKeepOriginFileController(cloudLowerStl, f);
        clinicFilesIsViewerKeepOriginFileController(cloudLowerObj, f);
        clinicFilesIsViewerKeepOriginFileController(localLowerPly, f);
        clinicFilesIsViewerKeepOriginFileController(localLowerStl, f);
        clinicFilesIsViewerKeepOriginFileController(localLowerObj, f);
      }
      if (type === 'OCCLUSION') {
        clinicFilesIsViewerKeepOriginFileController(cloudEtcModelPly, f);
        clinicFilesIsViewerKeepOriginFileController(cloudEtcModelStl, f);
        clinicFilesIsViewerKeepOriginFileController(cloudEtcModelObj, f);
        clinicFilesIsViewerKeepOriginFileController(localEtcModelPly, f);
        clinicFilesIsViewerKeepOriginFileController(localEtcModelStl, f);
        clinicFilesIsViewerKeepOriginFileController(localEtcModelObj, f);
      }
    });

    // 3) 데이터 반환
    return { count: localCount, chagedValue: next };
  };

  const handleScanFiilAutoView = (
    upperFiles: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    lowerFiles: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    occlusionFiles: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
  ) => {
    const urlWithoutQuery = window.location.origin + window.location.pathname; // Query string 제외한 URL 생성
    if (!queryPageTab) return;
    window.history.replaceState({}, document.title, urlWithoutQuery);
    upperFiles.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        console.log('HDS] upperFiles isView __ ', curr.isView);
        if (!curr.isView) {
          console.log('HDS] upperFiles isShow __ ', curr.isShow);
          if (!!curr.isShow && curr.isShow === 1) {
            console.log('HDS] occlusionFiles 여기 들어와 __ ');
            curr.isView = true;
            handleCombineViewAutoFile(true, curr);
          } else {
            curr.isView = false;
          }
        }
        return acc.concat(curr);
      }, []);
    });
    lowerFiles.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        console.log('HDS] lowerFiles isView __ ', curr.isView);
        if (!curr.isView) {
          console.log('HDS] lowerFiles isShow __ ', curr.isShow);
          if (!!curr.isShow && curr.isShow === 1) {
            console.log('HDS] occlusionFiles 여기 들어와 __ ');
            curr.isView = true;
            handleCombineViewAutoFile(true, curr);
          } else {
            curr.isView = false;
          }
        }
        return acc.concat(curr);
      }, []);
    });
    occlusionFiles.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        console.log('HDS] occlusionFiles isView __ ', curr.isView);
        if (!curr.isView) {
          console.log('HDS] occlusionFiles isShow __ ', curr.isShow);
          if (!!curr.isShow && curr.isShow === 1) {
            console.log('HDS] occlusionFiles 여기 들어와 __ ');
            curr.isView = true;
            handleCombineViewAutoFile(true, curr);
          } else {
            curr.isView = false;
          }
        }
        return acc.concat(curr);
      }, []);
    });
    // if (clinicViewModelFiles.value.length === scanShowiles.value.length) {
    //   console.log('HDS] setQueryPageTab >>>>>>>> undefined ');
    //   setQueryPageTab(0);
    // }
  };
  const clinicFilesIsViewerKeepOriginFileController = (
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.compareKey === file.compareKey) {
          curr.isView = file.isView;
        }
        return acc.concat(curr);
      }, []);
    });
  };

  const clinicFilesIsViewerOriginFileController = (
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.compareKey === file.compareKey) {
          curr.isView = !file.isView;
        }
        return acc.concat(curr);
      }, []);
    });
  };

  const clinicFilesIsViewerController = (
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    target: IConvertFile,
  ) => {
    if (target?.compareKey == null) return; // 방어코드
    let updatedItem: IConvertFile | null = null;
    const src = fileList.value ?? [];

    // 1) 계산: next 상태와 count, 사이드이펙트 대상 수집
    const next = src.map((item, idx) => {
      if (item.compareKey !== target.compareKey) return item;
      const nextIsView = !item.isView; // 현재 아이템 기준으로 토글
      const returnValue: IConvertFile = { ...item, isView: nextIsView };
      updatedItem = returnValue; // 사이드이펙트 대상 저장
      return returnValue;
    });

    // ===== 상태 커밋 이후 사이드이펙트 1회 실행 =====
    if (updatedItem && 'isView' in updatedItem) {
      handleCombineViewFile(!!(updatedItem as IConvertFile).isView, updatedItem as IConvertFile);
    }
    fileList.setValue(next);
  };

  const clinicFilesViewerClearAllController = (fileList: {
    value: IConvertFile[];
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  }) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isView = false;
        return acc.concat(curr);
      }, []);
    });
  };

  const handleClickView = (file: IConvertFile, location: 'partner' | 'group' | 'local') => {
    setModelError(null);
    switch (file.fileType) {
      case 'UPPER':
        clinicFilesIsViewerController(upperFiles, file);
        clinicFilesIsViewerOriginFileController(cloudUpperPly, file);
        clinicFilesIsViewerOriginFileController(cloudUpperStl, file);
        clinicFilesIsViewerOriginFileController(cloudUpperObj, file);
        clinicFilesIsViewerOriginFileController(localUpperPly, file);
        clinicFilesIsViewerOriginFileController(localUpperStl, file);
        clinicFilesIsViewerOriginFileController(localUpperObj, file);
        clinicFilesViewerClearAllController(photoFiles);
        clinicFilesViewerClearAllController(etcFiles);
        return;
      case 'LOWER':
        clinicFilesIsViewerController(lowerFiles, file);
        clinicFilesIsViewerOriginFileController(cloudLowerPly, file);
        clinicFilesIsViewerOriginFileController(cloudLowerStl, file);
        clinicFilesIsViewerOriginFileController(cloudLowerObj, file);
        clinicFilesIsViewerOriginFileController(localLowerPly, file);
        clinicFilesIsViewerOriginFileController(localLowerStl, file);
        clinicFilesIsViewerOriginFileController(localLowerObj, file);
        clinicFilesViewerClearAllController(photoFiles);
        clinicFilesViewerClearAllController(etcFiles);
        return;
      case 'OCCLUSION':
        clinicFilesIsViewerController(occlusionFiles, file);
        clinicFilesIsViewerOriginFileController(cloudOcclusionPly, file);
        clinicFilesIsViewerOriginFileController(cloudOcclusionStl, file);
        clinicFilesIsViewerOriginFileController(cloudOcclusionObj, file);
        clinicFilesIsViewerOriginFileController(localOcclusionPly, file);
        clinicFilesIsViewerOriginFileController(localOcclusionStl, file);
        clinicFilesIsViewerOriginFileController(localOcclusionObj, file);
        clinicFilesViewerClearAllController(photoFiles);
        clinicFilesViewerClearAllController(etcFiles);
        return;
      case 'NONE':
        clinicFilesIsViewerController(noneFiles, file);
        clinicFilesIsViewerOriginFileController(cloudEtcModelPly, file);
        clinicFilesIsViewerOriginFileController(cloudEtcModelStl, file);
        clinicFilesIsViewerOriginFileController(cloudEtcModelObj, file);
        clinicFilesIsViewerOriginFileController(localEtcModelPly, file);
        clinicFilesIsViewerOriginFileController(localEtcModelStl, file);
        clinicFilesIsViewerOriginFileController(localEtcModelObj, file);
        clinicFilesViewerClearAllController(photoFiles);
        clinicFilesViewerClearAllController(etcFiles);
        return;
      case 'PHOTO':
        clinicFilesIsViewerController(photoFiles, file);
        clinicFilesIsViewerOriginFileController(cloudPhotoFiles, file);
        clinicFilesIsViewerOriginFileController(localPhotoFiles, file);
        clinicFilesViewerClearAllController(upperFiles);
        clinicFilesViewerClearAllController(lowerFiles);
        clinicFilesViewerClearAllController(occlusionFiles);
        return;
      case 'ETC':
        clinicFilesIsViewerController(etcFiles, file);
        clinicFilesIsViewerOriginFileController(cloudEtcFiles, file);
        clinicFilesIsViewerOriginFileController(localEtcFiles, file);
        clinicFilesViewerClearAllController(upperFiles);
        clinicFilesViewerClearAllController(lowerFiles);
        clinicFilesViewerClearAllController(occlusionFiles);
        return;
      default:
        return;
    }
  };

  const handleClearAllView = () => {
    // clinicFilesViewerClearAllController(upperFiles);
    // clinicFilesViewerClearAllController(lowerFiles);
    // clinicFilesViewerClearAllController(occlusionFiles);
    // clinicFilesViewerClearAllController(noneFiles);
    // clinicFilesViewerClearAllController(photoFiles);
    // clinicFilesViewerClearAllController(etcFiles);
    clinicFilesViewerClearAllController(cloudUpperPly);
    clinicFilesViewerClearAllController(cloudUpperStl);
    clinicFilesViewerClearAllController(cloudUpperObj);
    clinicFilesViewerClearAllController(localUpperPly);
    clinicFilesViewerClearAllController(localUpperStl);
    clinicFilesViewerClearAllController(localUpperObj);

    clinicFilesViewerClearAllController(cloudLowerPly);
    clinicFilesViewerClearAllController(cloudLowerStl);
    clinicFilesViewerClearAllController(cloudLowerObj);
    clinicFilesViewerClearAllController(localLowerPly);
    clinicFilesViewerClearAllController(localLowerStl);
    clinicFilesViewerClearAllController(localLowerObj);

    clinicFilesViewerClearAllController(cloudOcclusionPly);
    clinicFilesViewerClearAllController(cloudOcclusionStl);
    clinicFilesViewerClearAllController(cloudOcclusionObj);
    clinicFilesViewerClearAllController(localOcclusionPly);
    clinicFilesViewerClearAllController(localOcclusionStl);
    clinicFilesViewerClearAllController(localOcclusionObj);

    clinicFilesViewerClearAllController(cloudEtcModelPly);
    clinicFilesViewerClearAllController(cloudEtcModelStl);
    clinicFilesViewerClearAllController(cloudEtcModelObj);
    clinicFilesViewerClearAllController(localEtcModelPly);
    clinicFilesViewerClearAllController(localEtcModelStl);
    clinicFilesViewerClearAllController(localEtcModelObj);

    clinicFilesViewerClearAllController(cloudPhotoFiles);
    clinicFilesViewerClearAllController(localPhotoFiles);

    clinicFilesViewerClearAllController(cloudEtcFiles);
    clinicFilesViewerClearAllController(localEtcFiles);

    clinicViewModelFiles.setValue([]);
    clinicViewHtmlFiles.setValue(null);
    clinicViewImageFiles.setValue(null);
  };

  const fetchCloudFile = async (file: IConvertFile, isImage: boolean) => {
    if (!file.cloudDataIdx) return;

    try {
      const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: file.cloudDataIdx });
      const { signedUrl } = data;

      if (!signedUrl) return;

      const { data: fileData } = await axs({
        url: signedUrl,
        method: 'get',
        responseType: 'blob',
      });

      if (!fileData) return;

      if (!isImage) {
        const htmlFile = new File([fileData], '', {
          type: 'text/html',
        });
        if (!file) return;
        const htmlUrl = URL.createObjectURL(htmlFile);
        // clinicViewHtmlFiles.setValue(htmlUrl);
        clinicViewHtmlFiles.setValue({ ...file, htmlUrl: htmlUrl });
      } else {
        const imageFile = await getFileDataUrl(fileData);
        // clinicViewImageFiles.setValue(imageFile);
        clinicViewImageFiles.setValue({ ...file, imgUrl: imageFile });
      }
    } catch (error) {
      AppActions.show_toast({
        type: 'error',
        message: (error as { message: string })?.message,
      });
    }
  };

  const fetchLocalFile = async (file: IConvertFile, isImage: boolean) => {
    if (!ipcRenderer) return;
    if (!isImage) {
      const localFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
        projectId,
        fileName: file.name,
      });
      const fileBlob = new Blob([new Uint8Array(localFileLoad?.fileData)], {
        type: 'text/html',
      });
      const fileData = new File([fileBlob], file.name, {
        type: 'text/html',
      });
      if (!fileData) return;
      const htmlUrl = URL.createObjectURL(fileData);
      // clinicViewHtmlFiles.setValue(htmlUrl);
      clinicViewHtmlFiles.setValue({
        ...file,
        htmlUrl: htmlUrl,
      });
    } else {
      ipcRenderer.send('projectFileLocalLoad', {
        projectId,
        fileName: file.name,
      });

      ipcRenderer.once('projectFileLocalLoadRes', async (e: any, data: any) => {
        const fileBlob = new Blob([new Uint8Array(data?.fileData)], { type: file.type });
        const fileData = new File([fileBlob], file.name, {
          type: file.type,
        });

        const dataUrl = await getFileDataUrl(fileData);
        // clinicViewImageFiles.setValue(dataUrl);
        clinicViewImageFiles.setValue({ ...file, imgUrl: dataUrl });
        clinicViewHtmlFiles.setValue(null);
      });
    }
  };

  const handleClickSyncNewPartner = (fileList: IConvertFile[]) => {
    if (!fileList?.length) {
      ProjectActions.create_project_sync_request({
        projectCode,
        partnerGroupIdx: selectedSyncPartner,
        cloudDataList: [],
      });
    } else {
      // 파일 포함 중복 체크
      handleCheckDuplicateUploadFile('partner', fileList);
      // setNewSyncPartner(selectedSyncPartner);
      // setUploadShareType(2);
      // handleClickUploadToPartner(fileList);
    }
  };

  const handleClickUploadToPartner = (fileList: IConvertFile[]) => {
    console.log('HDS] handleClickUploadToPartner _______ ', fileList);
    const locatedGroup = fileList.filter((file: IConvertFile) => {
      return !!file.isOwnerStorage && !file.isLocalStorage;
    });
    const locatedLocal = fileList.filter((file: IConvertFile) => {
      return !!file.isLocalStorage;
    });

    if (locatedGroup.length > 0) {
      const fileIdxList = locatedGroup.reduce((acc: any, curr: IConvertFile) => {
        if (curr.isChecked) {
          return acc.concat(curr.cloudDataIdx);
        }
        return acc;
      }, []);

      FileActions.edit_project_share_request({
        projectCode: projectCode,
        cloudDataList: fileIdxList,
        isShare: 2,
      });
    }
    if (locatedLocal.length > 0) {
      setUploadShareType(2);
      getLoadLocalFiles(locatedLocal);
    }
  };

  const handleClickUploadToGroup = () => {
    //Group Upload
    setUploadShareType(1);
    getLoadLocalFiles(checkedClinicFiles.value);
  };

  const getLoadLocalFiles = async (fileList: IConvertFile[]) => {
    console.log('HDS] getLoadLocalFiles _____ ', fileList);
    if (!ipcRenderer) return;
    const uploadFileData: any = [];
    fileList.map(async (file: IConvertFile) => {
      // const { type, extension } = file;
      const type = file.type;
      const extension = file.extension;
      const name = file.name.normalize('NFC');

      const fileItem = {
        ...file,
        type,
        name,
        extension,
      };

      const localFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
        projectId,
        fileName: name,
      });
      const fileBlob = new Blob([new Uint8Array(localFileLoad?.fileData)], { type });
      const fileData = new File([fileBlob], name, {
        type: extension,
      });
      const result = {
        ...fileItem,
        file: fileData,
      };
      // uploadFileData.push(result);
      clinicLoadLocalFilesDataList.setValue((draft: IConvertFile[]) => [...draft, result]);
    });
  };

  const handleRemoveViewLocalFileController = () => {
    deleteLocalFileList.value.map((deleteName: string) => {
      if (clinicViewModelFiles.value.length > 0) {
        const filterModelFile = clinicViewModelFiles.value.filter(
          (file: IConvertFile) => !!file.isOwnerStorage || file.originName !== deleteName,
        );
        clinicViewModelFiles.setValue(filterModelFile);
      }

      if (
        !!clinicViewImageFiles.value &&
        !clinicViewImageFiles.value.isOwnerStorage &&
        clinicViewImageFiles.value.originName === deleteName
      ) {
        clinicViewImageFiles.setValue(null);
      }

      if (
        !!clinicViewHtmlFiles.value &&
        !clinicViewHtmlFiles.value.isOwnerStorage &&
        clinicViewHtmlFiles.value.originName === deleteName
      ) {
        clinicViewHtmlFiles.setValue(null);
      }
    });
  };

  const fileOpacityChangeController = (
    opacity: number,
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    clinicViewModelFiles.value.map((item: any) => {
      if (
        (item.cloudDataIdx === file.cloudDataIdx || item.id === file.id) &&
        // item.hash === file.hash &&
        item.originName === file.originName
      ) {
        // item.material.opacity = opacity / 100;
        item.material.uniforms.opacity.value = opacity / 100;
        if (opacity === 100) {
          item.material.depthWrite = true;
        } else {
          item.material.depthWrite = false;
        }
      }
    });
  };

  const fileOpacityOriginChangeController = (
    opacity: number,
    fileList: {
      value: IConvertFile[];
      onChange: (e: any) => void;
      setValue: React.Dispatch<any>;
    },
    file: IConvertFile,
  ) => {
    fileList.setValue((draft: IConvertFile[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (
          (curr.cloudDataIdx === file.cloudDataIdx || curr.id === file.id) &&
          curr.originName === file.originName
        ) {
          curr.opacity = opacity;
        }
        return acc.concat(curr);
      }, []);
    });
  };

  const handleChangeModelOpacity = (opacity: number, file: IConvertFile) => {
    switch (file.fileType) {
      case 'UPPER':
        fileOpacityChangeController(opacity, upperFiles, file);
        fileOpacityOriginChangeController(opacity, cloudUpperPly, file);
        fileOpacityOriginChangeController(opacity, cloudUpperStl, file);
        fileOpacityOriginChangeController(opacity, cloudUpperObj, file);
        fileOpacityOriginChangeController(opacity, localUpperPly, file);
        fileOpacityOriginChangeController(opacity, localUpperStl, file);
        fileOpacityOriginChangeController(opacity, localUpperObj, file);
        return;
      case 'LOWER':
        fileOpacityChangeController(opacity, lowerFiles, file);
        fileOpacityOriginChangeController(opacity, cloudLowerPly, file);
        fileOpacityOriginChangeController(opacity, cloudLowerStl, file);
        fileOpacityOriginChangeController(opacity, cloudLowerObj, file);
        fileOpacityOriginChangeController(opacity, localLowerPly, file);
        fileOpacityOriginChangeController(opacity, localLowerStl, file);
        fileOpacityOriginChangeController(opacity, localLowerObj, file);
        return;
      case 'OCCLUSION':
        fileOpacityChangeController(opacity, occlusionFiles, file);
        fileOpacityOriginChangeController(opacity, cloudOcclusionPly, file);
        fileOpacityOriginChangeController(opacity, cloudOcclusionStl, file);
        fileOpacityOriginChangeController(opacity, cloudOcclusionObj, file);
        fileOpacityOriginChangeController(opacity, localOcclusionPly, file);
        fileOpacityOriginChangeController(opacity, localOcclusionStl, file);
        fileOpacityOriginChangeController(opacity, localOcclusionObj, file);
        return;
      case 'NONE':
        fileOpacityChangeController(opacity, noneFiles, file);
        fileOpacityOriginChangeController(opacity, cloudEtcModelPly, file);
        fileOpacityOriginChangeController(opacity, cloudEtcModelStl, file);
        fileOpacityOriginChangeController(opacity, cloudEtcModelObj, file);
        fileOpacityOriginChangeController(opacity, localEtcModelPly, file);
        fileOpacityOriginChangeController(opacity, localEtcModelStl, file);
        fileOpacityOriginChangeController(opacity, localEtcModelObj, file);
        return;
      case 'PHOTO':
        fileOpacityChangeController(opacity, photoFiles, file);
        return;
      case 'ETC':
        fileOpacityChangeController(opacity, etcFiles, file);
        return;
      default:
        return;
    }
  };

  const handleClickFileDownload = async () => {
    // if (!ipcRenderer) return;
    handleClearAllCheckbox();
    // checkedClinicFiles.value.forEach(async (file: IConvertFile) => {
    for (const file of checkedClinicFiles.value) {
      if (!file.cloudDataIdx) return;
      try {
        const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: file.cloudDataIdx });
        const { signedUrl } = data;
        if (ipcRenderer) {
          const uid = uuid();

          ipcRenderer.send('projectFileDownload', {
            url: signedUrl,
            properties: {
              projectCode,
              projectId: projectId.normalize('NFC'),
              fileName: file.originName.normalize('NFC'),
              uid,
            },
          });

          // NOTE: Electron download
          await newDownloadFileWithElectron({
            name: file.originName.normalize('NFC'),
            url: signedUrl,
            properties: {
              projectCode,
              projectId: projectInfo.projectId.normalize('NFC'),
              fileName: file.originName.normalize('NFC'),
            },
            type: 'download',
            uid,
          });
        } else {
          downloadFile({
            url: signedUrl,
            name: file.originName.normalize('NFC'),
          });
        }
      } catch (error) {
        AppActions.show_toast({
          type: 'error',
          message: (error as { message: string })?.message,
        });
      }

      handleCreateProjectHistory();
    }
    // handleClearAllCheckbox();
  };

  const handleCreateProjectHistory = () => {
    console.log('HDS] handleCreateProjectHistory ____');
    let name = '';
    checkedClinicFiles.value.forEach((item: IConvertFile, idx: number) => {
      if (idx === 0) {
        name += `${item.originName}`;
      } else {
        name += `, ${item.originName}`;
      }
    });
    const createHistoryData = {
      projectCode,
      type: projectEventType.fileDownload,
      params: {
        content: `${user.company}, ${name}`,
      },
    };
    ProjectActions.create_project_history_request(createHistoryData);
  };

  const handleOpenFileRemove = () => {
    setIsOpenRemoveModal(true);
  };
  const handleCloseFileRemove = () => {
    setIsOpenRemoveModal(false);
  };

  const handleConfirmFileRemove = () => {
    setIsOpenRemoveModal(false);
    if (!removeType) return;
    handleRemoveFile(removeType);
  };

  const handleRemoveFile = (location: string) => {
    const deleteIdxList = checkedClinicFiles.value.map((item: IConvertFile) => {
      return item.cloudDataIdx;
    });

    const deleteNameList = checkedClinicFiles.value.map((item: IConvertFile) => {
      return item.name;
    });
    if (location === 'partner') {
      FileActions.edit_project_share_request({
        projectCode,
        cloudDataList: deleteIdxList,
        isShare: 1,
      });
    } else if (location === 'group') {
      handleRemoveCloudFiles(deleteIdxList);
    } else if (location === 'local') {
      handleRemoveLocalFiles(deleteNameList);
    } else {
      handleRemoveCloudFiles(deleteIdxList);
      handleRemoveLocalFiles(deleteNameList);
    }
  };

  const handleRemoveCloudFiles = (deleteIdxList: number[]) => {
    let name = '';
    checkedClinicFiles.value.forEach((item: IConvertFile, idx: number) => {
      if (idx === 0) {
        name += `${item.originName}`;
      } else {
        name += `, ${item.originName}`;
      }
    });
    const submitData = {
      projectCode,
      cloudFileList: deleteIdxList,
    };
    deleteCloudFileList.setValue(deleteIdxList);
    FileActions.delete_project_file_request(submitData);

    const createHistoryData = {
      projectCode,
      type: projectEventType.fileDelete,
      params: {
        content: `${user.company}, ${name}`,
      },
    };
    ProjectActions.create_project_history_request(createHistoryData);
  };

  const handleRemoveLocalFiles = (deleteNameList: string[]) => {
    if (!ipcRenderer) return;
    deleteLocalFileList.setValue(deleteNameList);
    deleteNameList.map((item: string) => {
      setLocalFileDeleteSuccess(false);
      ipcRenderer.send('projectFileLocalDelete', {
        projectId: projectInfo.projectId,
        fileName: item,
      });
    });
  };

  const handleRemoveViewCloudFileController = () => {
    deleteCloudFileList.value.map((deleteIdx: number) => {
      if (clinicViewModelFiles.value.length > 0) {
        const filterModelFile = clinicViewModelFiles.value.filter(
          (file: IConvertFile) => !!file.isLocalStorage || file.cloudDataIdx !== deleteIdx,
        );
        clinicViewModelFiles.setValue(filterModelFile);
      }

      if (
        !!clinicViewImageFiles.value &&
        !clinicViewImageFiles.value.isLocalStorage &&
        clinicViewImageFiles.value.cloudDataIdx === deleteIdx
      ) {
        clinicViewImageFiles.setValue(null);
      }

      if (
        !!clinicViewHtmlFiles.value &&
        !clinicViewHtmlFiles.value.isLocalStorage &&
        clinicViewHtmlFiles.value.cloudDataIdx === deleteIdx
      ) {
        clinicViewHtmlFiles.setValue(null);
      }
    });
  };

  const handleCloseDuplicateModal = () => {
    setIsOpenDuplicateModal(false);
  };

  const handleConfirmDuplicateModal = (tyep: 'partner' | 'group') => {
    console.log('HDS] handleConfirmDuplicateModal _________ ', projectInfo.partnershipCode);

    console.log('HDS] handleConfirmDuplicateModal tyep_________ ', tyep);
    if (tyep === 'partner') {
      // handleConfirmFileRemove;
      console.log(
        'HDS]] clinicSortedAllLocalFileList ________ ',
        clinicSortedAllLocalFileList.value,
      );
      if (clinicSortedAllLocalFileList.value.length > 0) {
        // 로컬파일 있으면 (로컬파일업로드 버튼 눌러 들어온 경우)
        console.log('HDS] CLICK 전체 로컬 파일 ____________ ');
        if (!projectInfo.partnershipCode) {
          // 파트너 존재 여부 확인
          console.log('HDS] CLICK 전체 로컬 파일 파트너 [[ 없음 ]] ____________ ');
          setNewSyncPartner(selectedSyncPartner);
          setUploadShareType(2);
        } else {
          console.log('HDS] CLICK 전체 로컬 파일 파트너 [[ 존재 ]] ____________ ');
        }
        handleClickUploadToPartner(clinicSortedAllLocalFileList.value);
      } else {
        console.log('HDS] CLICK 일반 PARTNER SYNC  ____________ ');
        handleClickUploadToPartner(checkedClinicFiles.value);
      }
    } else {
      handleClickUploadToGroup();
    }
    setIsOpenDuplicateModal(false);
  };

  const handleCheckDuplicateUploadFile = (type: 'partner' | 'group', fileList: IConvertFile[]) => {
    // const cloudExist = fileList.find((file: IConvertFile) => !!file.isOwnerStorage);
    const cloudExist = fileList.find((file: IConvertFile) =>
      type === 'group' ? !!file.isOwnerStorage : !!file.isShareStorage,
    );

    if (!!cloudExist) {
      setIsOpenDuplicateModal(true);
      setIsOpenDuplicateModalType(type);
    } else if (type === 'partner') {
      if (!projectInfo.partnershipCode) {
        setNewSyncPartner(selectedSyncPartner);
        setUploadShareType(2);
      }
      handleClickUploadToPartner(fileList);
    } else {
      handleClickUploadToGroup();
    }
  };

  const handleClickAllLocalFileUploadToPartner = (isPartnershipCode: boolean) => {
    const allFiles = [
      ...upperFiles.value,
      ...lowerFiles.value,
      ...occlusionFiles.value,
      ...noneFiles.value,
      ...photoFiles.value,
      ...etcFiles.value,
    ];

    checkedClinicFiles.setValue([]);
    const sortLocalFile = allFiles.filter((file: IConvertFile) => {
      return !!file.isLocalStorage;
    });
    console.log(
      'HDS] handleClickAllLocalFileUploadToPartner [[ sortLocalFile ]]______ ',
      sortLocalFile,
    );
    const cloudExist = sortLocalFile.find((file: IConvertFile) => !!file.isOwnerStorage);
    if (!!cloudExist) {
      setIsOpenDuplicateModal(true);
      setIsOpenDuplicateModalType('partner');
      clinicSortedAllLocalFileList.setValue(sortLocalFile);
    } else {
      if (!!isPartnershipCode) {
        handleClickUploadToPartner(sortLocalFile);
      } else {
        clinicSortedAllLocalFileList.setValue(sortLocalFile);
        handleClickSyncNewPartner(sortLocalFile);
      }
    }
    setNewSyncPartner(selectedSyncPartner);
    setUploadShareType(2);
    // // handleClickUploadToPartner(checkedClinicFiles.value);
  };

  return {
    handleChangeCheckbox,
    handleClickView,
    handleAddLocalFiles,
    updateLocalFileList,
    duplicateLocalFile,
    localFileUploadIpc,
    handleClickSyncNewPartner,
    handleClickUploadToGroup,
    handleClickUploadToPartner,
    handleClearAllCheckbox,
    handleClearAllView,
    handleRemoveViewLocalFileController,
    handleChangeModelOpacity,
    handleClickFileDownload,
    handleOpenFileRemove,
    handleCloseFileRemove,
    handleConfirmFileRemove,
    handleRemoveViewCloudFileController,
    handlePmsThumbnailAutoView,
    handleCombineViewFile,
    handleClickAllLocalFileUploadToPartner,
    handleCheckDuplicateUploadFile,
    handleCloseDuplicateModal,
    handleConfirmDuplicateModal,
    handleScanFiilAutoView,
    handleScanFiilAutoViewer,
  };
};
